import { useState, useEffect } from 'react';

import { saveAs } from 'file-saver';
import { uniqueId } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { fileManagerApis } from '../apis/fileManagerApis';
import { MIME_TYPE } from '../components/page-sections/file-manager/constants';
import { FILE_MANAGER_UPLOAD_TYPE } from '../constants';
import { getFileExtName } from '../helpers';
import useToast, { TOAST_TYPE } from './useToast';
import { buildPath, PRIVATE_ROUTE } from '../routes/routes';
import { getFilenameFromResponseHeader, sentenceCase } from '../utils/utils';

const useFileManager = (basePath, baseFolderChain, collection, subscript) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId, instanceId, gpsId } = useParams();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [error, setError] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [folderChain, setFolderChain] = useState(baseFolderChain);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const path = folderChain.reduce(
    (prev, curr) => `${prev}/${curr.name}`,
    [`${basePath}`]
  );

  /**
   * Fetches the list of files at a given location based on the current folder
   */
  const getFileList = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data: fileRes } = await fileManagerApis.getFileList(
        projectId,
        instanceId || gpsId,
        path,
        subscript
      );
      const fileWithExt = fileRes.map((file) => ({
        ...file,
        id: uniqueId(),
        ext: getFileExtName(file.name),
      }));
      setFileList(fileWithExt);
    } catch (error) {
      setError(true);
      showToast(
        TOAST_TYPE.ERROR,
        t('fetch', { ns: 'errors', content: t('file', { ns: 'fileBrowser' }) })
      );
    } finally {
      setLoading(false);
    }
  };

  /* 
  If you are redirected back to the file manager page from the editor page,
  you can persist at the most recent directory, by setting the folder chain
  from the navigation state to the useFileManager's state. We slice off the
  first value because when passing the folder chain to the editor page, we
  add in base path so it can make accurate calls to get the file. When passed
  back, we don't need it so *chop chop*.
  */
  useEffect(() => {
    if (location.state) {
      setFolderChain(location.state.folderChain.slice(1));
    }
  }, []);

  // Update the file list everytime the folder chain changes
  useEffect(() => {
    getFileList();
  }, [folderChain]);

  const changeFolder = (newFolderChain) => {
    // Always reset selected files before navigating to new folder
    setSelectedFiles([]);
    setFolderChain(newFolderChain);
  };

  const selectFiles = (files) => setSelectedFiles(files);

  /**
   * Creates a new directory at the current directory.
   * @param {String} name The name of the new directory.
   */
  const createFolder = async (name) => {
    setLoading(true);
    try {
      const newFolderLocation = `${path}/${name}`;
      await fileManagerApis.createFolder(
        projectId,
        instanceId || gpsId,
        newFolderLocation,
        subscript
      );
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('createSuccess', {
            ns: 'notifications',
            text: t('file', { ns: 'fields' }),
          })
        )
      );
      await getFileList();
    } catch {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('createError', {
            ns: 'notifications',
            text: t('file', { ns: 'fields' }),
          })
        )
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Uploads either up to 20 regular files, or one zip file. If a zip file is uploaded
   * with the correct parameters, the zip file will be unzipped and it's contents will be placed
   * in the current directory.
   * @param {Array} files The array of files to be uploaded.
   * @param {String} type The string letting the backend know to either simply upload or upload and unzip.
   */
  const upload = async (files, type) => {
    setLoading(true);

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      if (type === FILE_MANAGER_UPLOAD_TYPE.REG) {
        await fileManagerApis.uploadFiles(
          projectId,
          instanceId || gpsId,
          subscript,
          path,
          formData
        );
      } else if (type === FILE_MANAGER_UPLOAD_TYPE.ZIP) {
        fileManagerApis.uploadAndUnzipFiles(
          projectId,
          instanceId || gpsId,
          subscript,
          path,
          formData
        );
      }

      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('uploadSuccess', {
            ns: 'notifications',
            text: t('file', { ns: 'fields' }),
          })
        )
      );
      await getFileList();
    } catch {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('uploadError', {
            ns: 'notifications',
            text: t('file', { ns: 'fields' }),
          })
        )
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Renames a directory or file. Keep in mind that if a file is given the same name as a file in the same directory, it will overwrite that file.
   * @param {String} newName The new name of the directory or file.
   */
  const rename = async (newName) => {
    setLoading(true);
    try {
      const oldPath = `${path}/${selectedFiles[0].name}`;
      const newPath = `${path}/${newName}`;

      await fileManagerApis.renameFolderFile(
        projectId,
        instanceId || gpsId,
        oldPath,
        newPath,
        subscript
      );
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('updateSuccess', {
            ns: 'notifications',
            text: t('file', { ns: 'fields' }),
          })
        )
      );
      await getFileList();
    } catch {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('updateError', {
            ns: 'notifications',
            text: t('file', { ns: 'fields' }),
          })
        )
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Opens the currently selected file in the file editing page. Basically
   * just navigates while saving the state in the location.
   */
  const edit = () => {
    const editorPageState = {
      collection: collection,
      file: selectedFiles[0],
      /* We pass in the base path as well because the editor needs the full path when making the request to update the file. 
      When passed back, it is sliced off. See the first useEffect on page load for more info. */
      folderChain: [{ name: basePath }, ...folderChain],
      previousLocation: location.pathname,
      subscript: subscript,
    };

    if (instanceId) {
      const solrEditorPage = buildPath({
        path: PRIVATE_ROUTE.INSTANCE_FILE_EDITOR,
        params: { projectId, instanceId },
      });
      navigate(solrEditorPage, { state: editorPageState });
    } else if (gpsId) {
      const gpsEditorPage = buildPath({
        path: PRIVATE_ROUTE.GPS_FILE_EDITOR,
        params: { projectId, gpsId },
      });
      navigate(gpsEditorPage, { state: editorPageState });
    }
  };

  /**
   * Downloads the currently selected file or files. If one file is selected
   * it will download that file. If multiple files are selected, they will be zipped.
   */
  const download = async () => {
    setLoading(true);
    showToast(
      TOAST_TYPE.INFO,
      t('prepareForDownload', { ns: 'notifications' })
    );
    try {
      const isDownload = true;
      const fileNames = selectedFiles.map((file) => file.name).join(',');

      const response = await fileManagerApis.downloadFiles(
        projectId,
        instanceId || gpsId,
        isDownload,
        subscript,
        path,
        fileNames
      );
      const name = getFilenameFromResponseHeader(response.headers);
      const mimeType =
        selectedFiles.length > 1
          ? MIME_TYPE.zip
          : selectedFiles[0].dir
            ? MIME_TYPE.zip
            : selectedFiles[0].ext;
      const blob = new Blob([response.data], { type: mimeType });
      saveAs(blob, name);
      showToast(
        TOAST_TYPE.SUCCESS,
        t('downloadSuccess', { ns: 'notifications' })
      );
    } catch {
      showToast(TOAST_TYPE.ERROR, t('downloadError', { ns: 'notifications' }));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get the content of a file
   * @param file - The file information to get the content of
   * @param lastByte
   * @returns {Promise<{totalSize: number, lastByte, content: string}>} - The content of the file
   */
  const logs = async (file, lastByte) => {
    try {
      const chunkSize = 1024 * 20;
      // Request the new bytes starting from the last position
      const response = await fileManagerApis.getLogPartition(
        projectId,
        instanceId,
        subscript,
        lastByte,
        chunkSize,
        path,
        file.name
      );

      if (response.status === 206) {
        // Partial content response: Fetch new log data
        const contentRange = response.headers['x-content-range'];

        // Parse the Content-Range header to get the byte range and total file size
        const [, end, totalSize] = contentRange
          .match(/bytes (\d+)-(\d+)\/(\d+)/)
          .slice(1)
          .map(Number);

        const fileData = response.data;
        const decoder = new TextDecoder('utf-8');
        const newContent = decoder.decode(fileData);

        return {
          lastByte: end + 1,
          totalSize,
          content: newContent,
          canFetchMore: true,
        };
      } else {
        console.warn(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      if (error.response.status === 416) {
        return { lastByte, totalSize: 0, content: '', canFetchMore: false };
      } else {
        console.error('Error fetching log content:', error);
        showToast(
          TOAST_TYPE.ERROR,
          t('fetch', { ns: 'errors', content: file.name })
        );
        // Show proper toast message
      }
    }
  };

  /**
   * Deletes the currently selected files.
   */
  const deleteFiles = async () => {
    setLoading(true);
    try {
      const filesToDelete = selectedFiles.map((file) => file.name).join(', ');
      await fileManagerApis.deleteFolderFile(
        projectId,
        instanceId || gpsId,
        subscript,
        path,
        filesToDelete
      );
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('deleteSuccess', {
            ns: 'notifications',
            text: t('file', { ns: 'fields' }),
          })
        )
      );
      await getFileList();
    } catch {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('deleteError', {
            ns: 'notifications',
            text: t('file', { ns: 'fields' }),
          })
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const actions = {
    create: createFolder,
    delete: deleteFiles,
    download,
    edit,
    logs,
    rename,
    upload,
  };

  return {
    actions,
    changeFolder,
    error,
    fileList,
    folderChain,
    getFileList,
    loading,
    selectedFiles,
    selectFiles,
  };
};

export default useFileManager;
